import axios from 'axios';
import { 
  FilterOptions, 
  ProvidersResponse, 
  DatasetVersions, 
  LoginCredentials, 
  User,
  AuthResponse,
  CreateUserRequest,
  UpdateUserRequest,
  UserWithStatus,
  Provider
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://dev-api-doc-finder.labsquire.com/api';
console.log("===== ENV API URL", process.env.REACT_APP_API_URL);
console.log("===== USING API URL", API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

// Add request interceptor to log API calls and add auth token
api.interceptors.request.use(
  (config) => {
    console.log('API CALL:', config.method?.toUpperCase(), config.url, config.params || config.data);
    
    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    console.error('API REQUEST ERROR:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      // Let the app handle the redirect through React Router
    }
    return Promise.reject(error);
  }
);

export const apiService = {
  async getDatasets(): Promise<DatasetVersions> {
    const response = await api.get('/datasets');
    return response.data;
  },

  async getProviders(filters: FilterOptions): Promise<ProvidersResponse> {
    const response = await api.get('/providers', { params: filters });
    return response.data;
  },

  async searchProviders(filters: FilterOptions): Promise<ProvidersResponse> {
    const response = await api.get('/providers', { params: filters });
    return response.data;
  },

  async getProviderByNPI(npi: string): Promise<Provider | null> {
    try {
      const response = await api.get('/providers', { 
        params: { npi, limit: 1, year: 2023 } 
      });
      return response.data.providers?.[0] || null;
    } catch (error) {
      return null;
    }
  },

  // Authentication
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  async verifyToken(): Promise<{ valid: boolean; user: User }> {
    const response = await api.get('/auth/verify');
    return response.data;
  },

  async getCurrentUser(): Promise<{ user: User }> {
    const response = await api.get('/auth/me');
    return response.data;
  },

  // User Management (Admin only)
  async getUsers(): Promise<{ users: UserWithStatus[] }> {
    const response = await api.get('/users');
    return response.data;
  },

  async createUser(userData: CreateUserRequest): Promise<{ message: string; user: User }> {
    const response = await api.post('/users', userData);
    return response.data;
  },

  async updateUser(userId: string, userData: UpdateUserRequest): Promise<{ message: string; user: UserWithStatus }> {
    const response = await api.put(`/users/${userId}`, userData);
    return response.data;
  },

  async deleteUser(userId: string): Promise<void> {
    await api.delete(`/users/${userId}`);
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const response = await api.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },

  // Visit methods
  async getVisits(params?: any): Promise<{ visits: any[]; pagination: any }> {
    const response = await api.get('/visits', { params });
    return response.data;
  },

  async getVisit(id: string): Promise<any> {
    const response = await api.get(`/visits/${id}`);
    return response.data;
  },

  async createVisit(visit: any): Promise<any> {
    const response = await api.post('/visits', visit);
    return response.data;
  },

  async updateVisit(id: string, visit: any): Promise<any> {
    const response = await api.put(`/visits/${id}`, visit);
    return response.data;
  },

  async deleteVisit(id: string): Promise<void> {
    await api.delete(`/visits/${id}`);
  },

  async addVisitComment(id: string, text: string): Promise<any> {
    const response = await api.post(`/visits/${id}/comments`, { text });
    return response.data;
  },

  async getVisitStats(params?: any): Promise<any> {
    const response = await api.get('/visits/stats', { params });
    return response.data;
  },

  async getUnhandledFollowUps(npi: string): Promise<{ visits: any[] }> {
    const response = await api.get(`/visits/providers/${npi}/unhandled-followups`);
    return response.data;
  },

  async createFollowUpVisit(visitData: any): Promise<any> {
    const response = await api.post('/visits/follow-up', visitData);
    return response.data;
  },

  async getVisitChain(visitId: string): Promise<any> {
    const response = await api.get(`/visits/${visitId}/chain`);
    return response.data;
  },
};
